import { observable, toJS, makeAutoObservable } from 'mobx';
import createDataProxy from 'main/components/tables/interactive/state/data_proxy';
import _ from 'lodash';
import saveStateToUrl from 'main/components/tables/interactive/state/saveStateToUrl';
import loadStateFromUrl from 'main/components/tables/interactive/state/loadStateFromUrl';

class InteractiveContextState {
  proxy;
  history;
  onRefresh;
  onMount;
  saveStateToUrl;
  latestRequestId = 0;

  isUpdating = false;
  isLoadingFirstTime = true;
  models = [];
  filter = observable.map();
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 0
  };
  sorting = observable.map();
  defaultFilter;
  isEmpty = false;
  onFilterUpdated;
  onPaginationUpdated;

  constructor({proxy, history, onRefresh, onMount, onFilterUpdated, onPaginationUpdated, saveStateToUrl}) {
    makeAutoObservable(this, {
      history: false
    });

    this.onFilterUpdated = onFilterUpdated;
    this.onPaginationUpdated = onPaginationUpdated;
    this.setProxy(proxy);
    this.history = history;
    this.onRefresh = onRefresh;
    this.onMount = onMount;
    this.saveStateToUrl = saveStateToUrl;
  }

  setProxy(proxy) {
    this.proxy = createDataProxy(proxy);
  }

  async load({initialFilter, initialSorting}) {
    const state = this.saveStateToUrl ? loadStateFromUrl(this.history) : {};

    const filter = _.merge(toJS(initialFilter), toJS(state.filter));
    const pagination = toJS(state.pagination);
    const sorting = _.merge(toJS(initialSorting), toJS(state.sorting));

    await this.refresh({
      filter,
      sorting,
      pagination
    });

    if (this.onMount) {
      await this.onMount({
        refresh: () => this.refresh(),
        isEmpty: this.isEmpty,
        models: this.models,
        filter: this.filter,
        pagination: this.pagination
      });
    }
  }

  async updateFilter(filter) {
    await this.refresh({
      filter,
      pagination: {
        currentPage: 1
      }
    });
    this.onFilterUpdated && (await this.onFilterUpdated(this.filter));
  }

  async deleteFilter(filterKey) {
    this.filter.delete(filterKey);
    await this.refresh();
    this.onFilterUpdated && this.onFilterUpdated(this.filter);
  }

  async updateSorting(sorting) {
    await this.refresh({
      sorting,
      pagination: {
        currentPage: 1
      }
    });
  }

  async updatePagination(pagination) {
    if (this.onPaginationUpdated) {
      this.onPaginationUpdated(pagination);
    }
    await this.refresh({pagination});
  }

  updateDefaultFilter(filter) {
    this.defaultFilter = _.merge(toJS(filter), this.defaultFilter);
  }

  async refresh(args = {}) {
    const requestId = this.latestRequestId + 1;
    this.latestRequestId = requestId;

    await this.executeRefresh(args, requestId);
  }

  async executeRefresh(args, requestId) {
    this.isUpdating = true;

    const {data, filter, pagination, sorting} = await this.proxy.load({
      filter: _.merge(Object.fromEntries(this.filter), args.filter),
      pagination: _.merge(this.pagination, args.pagination),
      sorting: _.merge(Object.fromEntries(this.sorting), args.sorting)
    });

    if (requestId !== this.latestRequestId) {
      return;
    }

    this.filter.merge(filter);
    this.sorting.merge(sorting);
    this.pagination = pagination;

    this.updateModels(data);

    this.isUpdating = false;
    this.isLoadingFirstTime = false;

    if (this.onRefresh) {
      this.onRefresh({
        data: data,
        filter: this.filter,
        sorting: this.sorting,
        pagination: this.pagination
      });
    }

    if (this.saveStateToUrl) {
      saveStateToUrl(this.history, this.filter, this.pagination, this.sorting, this.defaultFilter);
    }
  }

  updateModels(data = []) {
    this.models = data;

    if (_.isEmpty(data) && this.isInDefaultState()) {
      this.isEmpty = true;
    }
  }

  isInDefaultState() {
    if (this.filter) {
      for (const pair of this.filter.entries()) {
        if (!pair[1]) continue;
        if (_.get(this.defaultFilter, pair[0]) === pair[1]) continue;

        return false;
      }
    }

    if (this.sorting) {
      for (const pair of this.sorting.entries()) {
        if (!pair[1]) continue;

        return false;
      }
    }

    if (this.pagination && this.pagination.currentPage > 1) {
      return false;
    }

    return true;
  }
}

export default InteractiveContextState;
